import { isDevelopmentEnvironment } from '../utils/env-utils';
import { AdobeAppLabels, Comscore, QueryParams } from '../appTypings';
import adobeAnalytics from '../Player/analytics/adobeAnalytics';
import { hasDevelopmentConsent } from '../CookieConsent/utils';

export const hasADB = () => {
  return Boolean(typeof window !== 'undefined' && window.ADB);
};

const hasUtmParameters = (query: string) => query.includes('utm_source');

const getPageUrlContext = (): { pageURL: string } | undefined => {
  if (typeof window !== 'undefined' && window.location.origin) {
    return {
      pageURL:
        window.location.origin +
        window.location.pathname +
        (hasUtmParameters(window.location.search)
          ? window.location.search
          : ''),
    };
  }
};

const mapContextData = (labels: Partial<AdobeAppLabels>) => ({
  Resolution: labels.Resolution,
  DeviceName: labels.DeviceName,
  OSVersion: labels.OSVersion,
  yle_selva: labels.yle_selva,
  yle_client: labels.yle_client,
  yle_referer: labels.yle_referer,
  yle_pageloadid: labels.yle_pageloadid,
  AppID: labels.yle_client,
  yle_rec: labels.yle_rec,
  yle_ab_string: labels.yle_ab_string,
  userconsent: labels.userconsent,
  ...(labels.profile_age_group && {
    profile_age_group: labels.profile_age_group,
  }),
});

export const trackState = (
  comscore: Comscore,
  appLabels: AdobeAppLabels,
  utmParameters?: QueryParams
) => {
  if (hasADB() && !isDevelopmentEnvironment(window.location.hostname)) {
    adobeAnalytics.appMeasurement.contextData = {}; // clear context
    const {
      'yle.vrsDevice': yle_vrs_device,
      'yle.vrsPlatform': yle_vrs_platform,
      'yle.vrsProduct': yle_vrs_product,
      yle_selva,
      ...adobeContextLabels
    } = appLabels;

    const {
      countername,
      yle_sk,
      yle_id,
      yle_language,
      ...comscoreContextLabels
    } = comscore;

    const pageName = countername || comscore.yle_referer;
    const pageUrlContext = getPageUrlContext();

    adobeAnalytics.appMeasurement.pageName = pageName;
    adobeAnalytics.appMeasurement.prop50 = yle_vrs_product;
    adobeAnalytics.appMeasurement.prop51 = yle_vrs_device;
    adobeAnalytics.appMeasurement.prop52 = yle_vrs_platform;
    adobeAnalytics.appMeasurement.eVar2 = pageUrlContext?.pageURL;
    adobeAnalytics.appMeasurement.eVar12 = yle_sk;
    adobeAnalytics.appMeasurement.eVar14 = yle_selva;
    adobeAnalytics.appMeasurement.eVar15 = yle_id;
    adobeAnalytics.appMeasurement.eVar46 = yle_language;
    adobeAnalytics.appMeasurement.timestamp = new Date().getTime();

    adobeAnalytics.appMeasurement.contextData = {
      ...comscoreContextLabels,
      ...pageUrlContext,
      ...mapContextData(adobeContextLabels),
      ...utmParameters,
    };
    adobeAnalytics.appMeasurement.t();
  }
};

export const trackAction = (comscore: Comscore, appLabels: AdobeAppLabels) => {
  const { countername } = comscore;
  if (
    hasDevelopmentConsent() &&
    hasADB() &&
    !isDevelopmentEnvironment(window.location.hostname)
  ) {
    adobeAnalytics.appMeasurement.contextData = {}; // clear context

    adobeAnalytics.appMeasurement.pageName = countername;
    adobeAnalytics.appMeasurement.timestamp = new Date().getTime();
    adobeAnalytics.appMeasurement.contextData = {
      ...comscore,
      ...getPageUrlContext(),
      ...mapContextData(appLabels),
    };
    adobeAnalytics.appMeasurement.tl();
  }
};

export const trackSessionStart = (mediaInfo: any, contextData: any) => {
  if (hasADB() && !isDevelopmentEnvironment(window.location.hostname)) {
    adobeAnalytics.tracker.trackSessionStart(mediaInfo, {
      ...contextData,
      'yle.finnpanel': 'true',
    });
  }
};

export const trackSessionEnd = () => {
  if (hasADB() && !isDevelopmentEnvironment(window.location.hostname)) {
    adobeAnalytics.tracker.trackSessionEnd();
  }
};

export const trackComplete = () => {
  if (hasADB() && !isDevelopmentEnvironment(window.location.hostname)) {
    adobeAnalytics.tracker.trackComplete();
  }
};

export const trackPlay = () => {
  if (hasADB() && !isDevelopmentEnvironment(window.location.hostname)) {
    adobeAnalytics.tracker.trackPlay();
  }
};

export const trackPause = () => {
  if (hasADB() && !isDevelopmentEnvironment(window.location.hostname)) {
    adobeAnalytics.tracker.trackPause();
  }
};

export const trackEvent = (seekStart: any) => {
  if (hasADB() && !isDevelopmentEnvironment(window.location.hostname)) {
    adobeAnalytics.tracker.trackEvent(seekStart);
  }
};
